import { useEffect, useRef } from 'react';

import { extractArtifactInputsFromMessages } from '@erikdakoda/artifact/shared/extractArtifactInputsFromMessages';
import type { AiMessageForUi } from '@erikdakoda/convo/types';
import {
  clearConvoArtifactPanel,
  getConvoArtifactPanel,
  setConvoArtifactPanel,
} from '../shared/convoArtifactPanelStorage';
import { pushArtifactToPanel } from '../shared/pushArtifactToPanel';
import { useArtifactStore } from './useArtifactStore';

type UsePersistedConvoArtifactPanelParams = {
  convoId: string | undefined;
  messages: readonly AiMessageForUi[];
};

/**
 * Closes the artifact panel when leaving a convo, persists the open artifact per convo,
 * and restores it when the user returns.
 */
export function usePersistedConvoArtifactPanel({
  convoId,
  messages,
}: UsePersistedConvoArtifactPanelParams): void {
  const convoIdRef = useRef<string | undefined>(convoId);
  const restoredConvoIdRef = useRef<string | null>(null);
  const previousPanelOpenRef = useRef<boolean | null>(null);

  const panelOpen = useArtifactStore((state) => state.panelOpen);
  const activeIdentifier = useArtifactStore((state) => state.activeIdentifier);

  useEffect(() => {
    convoIdRef.current = convoId;
    restoredConvoIdRef.current = null;
    previousPanelOpenRef.current = null;
  }, [convoId]);

  useEffect(() => {
    if (!convoId) {
      return;
    }

    const previousPanelOpen = previousPanelOpenRef.current;
    previousPanelOpenRef.current = panelOpen;

    if (panelOpen && activeIdentifier) {
      setConvoArtifactPanel(convoId, activeIdentifier);
      return;
    }

    if (previousPanelOpen === true && !panelOpen) {
      clearConvoArtifactPanel(convoId);
    }
  }, [convoId, panelOpen, activeIdentifier]);

  useEffect(() => {
    if (!convoId || restoredConvoIdRef.current === convoId) {
      return;
    }

    const savedIdentifier = getConvoArtifactPanel(convoId);
    if (!savedIdentifier) {
      restoredConvoIdRef.current = convoId;
      return;
    }

    const {
      artifacts,
      panelOpen: isPanelOpen,
      activeIdentifier: currentIdentifier,
    } = useArtifactStore.getState();

    if (isPanelOpen && currentIdentifier === savedIdentifier) {
      restoredConvoIdRef.current = convoId;
      return;
    }

    const fromStore = artifacts[savedIdentifier];
    if (fromStore) {
      useArtifactStore.getState().openInPanel(savedIdentifier);
      restoredConvoIdRef.current = convoId;
      return;
    }

    if (messages.length === 0) {
      return;
    }

    const artifactInput = extractArtifactInputsFromMessages(messages).find(
      (artifact) => artifact.identifier === savedIdentifier,
    );

    if (!artifactInput) {
      restoredConvoIdRef.current = convoId;
      return;
    }

    pushArtifactToPanel({
      identifier: artifactInput.identifier,
      type: artifactInput.type,
      title: artifactInput.title,
      content: artifactInput.content,
      display:
        artifactInput.display === 'inline' || artifactInput.display === 'panel'
          ? artifactInput.display
          : undefined,
    });
    restoredConvoIdRef.current = convoId;
  }, [convoId, messages]);

  useEffect(() => {
    return () => {
      const id = convoIdRef.current;
      if (!id) {
        return;
      }

      const {
        panelOpen: isPanelOpen,
        activeIdentifier: currentIdentifier,
        closePanel,
      } = useArtifactStore.getState();

      if (isPanelOpen && currentIdentifier) {
        setConvoArtifactPanel(id, currentIdentifier);
      } else {
        clearConvoArtifactPanel(id);
      }

      closePanel();
    };
  }, []);
}
